import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../lib/theme';
import { useIsPhone } from '../lib/use-media';
import { Icon } from './Icon';

export interface ComboOption {
  value: string;
  /** Shown in the field and as the option's main line. */
  label: string;
  /** Second, muted line (e.g. "Bambu Lab · PLA · 640 g"). */
  detail?: ReactNode;
  /** Extra text the search matches (SKU, WhatsApp, spool code). */
  search?: string;
  /** Swatch, cover thumbnail, avatar… */
  icon?: ReactNode;
  disabled?: boolean;
  /** Always listed, whatever is typed ("Sem carretel", "Todos os canais"). */
  pinned?: 'top' | 'bottom';
}

/** Lists longer than this get a search box. */
export const SEARCH_FROM = 8;

const fold = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Every typed word must appear in the label, the detail text or the search text. */
export function matches(o: ComboOption, q: string): boolean {
  const words = fold(q).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = fold(
    [o.label, typeof o.detail === 'string' ? o.detail : '', o.search ?? ''].join(' '),
  );
  return words.every((w) => hay.includes(w));
}

function OptionBody({ o }: { o: ComboOption }) {
  return (
    <>
      {o.icon !== undefined ? <span className="combo-ic">{o.icon}</span> : null}
      <span className="col grow" style={{ minWidth: 0, gap: 0, alignItems: 'flex-start' }}>
        <span className="trunc" style={{ maxWidth: '100%' }}>
          {o.label}
        </span>
        {o.detail ? (
          <span className="xs muted trunc" style={{ maxWidth: '100%' }}>
            {o.detail}
          </span>
        ) : null}
      </span>
    </>
  );
}

/**
 * Searchable single choice with an icon per option, for lists that grow (filaments, spools,
 * products, customers…). A dropdown under the field on desktop, a bottom sheet on the phone.
 * The trigger is a button, so `<label htmlFor={id}>` labels it like a native select.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder = 'Escolher',
  title,
  ariaLabel,
  invalid,
  disabled,
  size = 'md',
  selected,
  onSearch,
  loading,
  emptyText = 'Nada encontrado.',
  searchPlaceholder = 'Buscar…',
  style,
}: {
  id?: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  options: ComboOption[];
  placeholder?: string;
  /** Sheet title on the phone; defaults to `ariaLabel` or the placeholder. */
  title?: string;
  ariaLabel?: string;
  invalid?: boolean;
  disabled?: boolean;
  size?: 'md' | 'sm';
  /** How to show the value when it is not among `options` (server search results). */
  selected?: ComboOption | null;
  /** Server-side search: called with what is typed; `options` are then the results. */
  onSearch?: (q: string) => void;
  loading?: boolean;
  emptyText?: string;
  searchPlaceholder?: string;
  style?: CSSProperties;
}) {
  const phone = useIsPhone();
  const { dz } = useTheme();
  const listId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const searchBox = useRef<HTMLInputElement>(null);
  const listBox = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<CSSProperties>({});

  const current =
    options.find((o) => o.value === value) ?? (selected?.value === value ? selected : null);
  const withSearch = !!onSearch || options.filter((o) => !o.pinned).length >= SEARCH_FROM;
  const visible = useMemo(() => {
    const top = options.filter((o) => o.pinned === 'top');
    const bottom = options.filter((o) => o.pinned === 'bottom');
    const rest = options.filter((o) => !o.pinned && (onSearch ? true : matches(o, q)));
    return [...top, ...rest, ...bottom];
  }, [options, q, onSearch]);
  const enabled = visible.filter((o) => !o.disabled);
  const hasResults = visible.some((o) => !o.pinned);

  // Server search, debounced.
  useEffect(() => {
    if (!onSearch || !open) return;
    const t = window.setTimeout(() => onSearch(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q, open, onSearch]);

  // Desktop: below the field, or above it when there is no room; follows scrolling.
  useLayoutEffect(() => {
    if (!open || phone) return;
    const place = () => {
      const r = trigger.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom;
      const up = below < 340 && r.top > below;
      setPos({
        left: Math.max(8, Math.min(r.left, window.innerWidth - Math.max(r.width, 280) - 8)),
        width: Math.max(r.width, 280),
        ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
        maxHeight: Math.max(160, Math.min(360, (up ? r.top : below) - 12)),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, phone]);

  useEffect(() => {
    if (!open) return;
    (withSearch ? searchBox.current : listBox.current)?.focus();
  }, [open, withSearch]);

  const show = (typed = '') => {
    if (disabled) return;
    setQ(typed);
    const i = enabled.findIndex((o) => o.value === value);
    setActive(Math.max(0, typed ? 0 : i));
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setQ('');
    trigger.current?.focus();
  };
  const choose = (o: ComboOption) => {
    if (o.disabled) return;
    onChange(o.value);
    close();
  };

  // Keys inside the open list (on the search box, or the list itself without one).
  const onListKey = (e: KeyboardEvent) => {
    const n = enabled.length;
    if (e.key === 'Escape') {
      // Only the picker closes, not the dialog around it.
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'ArrowDown' && n) {
      e.preventDefault();
      setActive((a) => (a + 1) % n);
    } else if (e.key === 'ArrowUp' && n) {
      e.preventDefault();
      setActive((a) => (a - 1 + n) % n);
    } else if (e.key === 'Home' && n) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End' && n) {
      e.preventDefault();
      setActive(n - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = enabled[active];
      if (o) choose(o);
    } else if (e.key === 'Tab') {
      setOpen(false);
      setQ('');
    }
  };

  // Keys on the closed field: arrows, Enter and Space open it; typing opens it searching.
  const onTriggerKey = (e: KeyboardEvent) => {
    if (open) return;
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      show();
    } else if (withSearch && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      show(e.key);
    }
  };

  useEffect(() => {
    if (!open) return;
    listBox.current
      ?.querySelector<HTMLElement>(`[data-i="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const activeOption = enabled[active];
  const list = (
    <div
      ref={listBox}
      id={listId}
      role="listbox"
      aria-label={title ?? ariaLabel ?? placeholder}
      tabIndex={withSearch ? undefined : -1}
      onKeyDown={withSearch ? undefined : onListKey}
      className={phone ? 'col' : undefined}
      style={
        phone
          ? { gap: 8, overflowY: 'auto', maxHeight: '55vh' }
          : { overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }
      }
    >
      {visible.map((o) => {
        const i = enabled.indexOf(o);
        const on = o.value === value;
        return (
          <button
            key={`${o.pinned ?? ''}${o.value}`}
            type="button"
            role="option"
            id={`${listId}-${i}`}
            data-i={i}
            aria-selected={on}
            aria-disabled={o.disabled || undefined}
            disabled={o.disabled}
            className={[
              phone ? 'pick' : 'combo-opt',
              on ? 'on' : '',
              !phone && i === active ? 'hot' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onMouseEnter={() => i >= 0 && setActive(i)}
            onClick={() => choose(o)}
          >
            <OptionBody o={o} />
            {on ? <Icon name="check" size={16} /> : null}
          </button>
        );
      })}
      {!hasResults ? (
        <p className="sm muted" role="status" style={{ padding: '12px' }}>
          {loading ? 'Buscando…' : emptyText}
        </p>
      ) : null}
    </div>
  );
  const search = withSearch ? (
    <label className="search" style={{ maxWidth: 'none', flex: '0 0 auto' }}>
      <Icon name="search" size={18} />
      <input
        ref={searchBox}
        aria-label={searchPlaceholder}
        placeholder={searchPlaceholder}
        role="searchbox"
        aria-controls={listId}
        aria-activedescendant={activeOption ? `${listId}-${active}` : undefined}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
        }}
        onKeyDown={onListKey}
      />
    </label>
  ) : null;

  return (
    <>
      <button
        ref={trigger}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        className={['input', 'combo', size === 'sm' ? 'sm' : '', invalid ? 'err' : '']
          .filter(Boolean)
          .join(' ')}
        style={style}
        onClick={() => (open ? close() : show())}
        onKeyDown={onTriggerKey}
      >
        {current?.icon !== undefined && current ? (
          <span className="combo-ic">{current.icon}</span>
        ) : null}
        <span className={current ? 'grow trunc' : 'grow trunc muted'}>
          {current?.label ?? placeholder}
        </span>
        <Icon name="chevronDown" size={16} />
      </button>
      {open
        ? createPortal(
            <div className={dz} style={{ background: 'transparent' }}>
              {phone ? (
                <div style={{ position: 'fixed', inset: 0, zIndex: 130 }}>
                  <div className="sheetwrap">
                    <button
                      type="button"
                      className="scrimbtn"
                      aria-label="Fechar"
                      tabIndex={-1}
                      onClick={close}
                    />
                    <div
                      className="sheet col"
                      role="dialog"
                      aria-modal="true"
                      aria-label={title ?? ariaLabel ?? placeholder}
                      style={{ gap: 12, position: 'relative' }}
                    >
                      <div className="grab" style={{ marginBottom: 0 }} />
                      <h2 style={{ fontSize: 18 }}>{title ?? ariaLabel ?? placeholder}</h2>
                      {search}
                      {list}
                      <button type="button" className="btn block" onClick={close}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="scrimbtn"
                    aria-label="Fechar lista"
                    tabIndex={-1}
                    style={{ position: 'fixed', inset: 0, zIndex: 129, background: 'transparent' }}
                    onClick={close}
                  />
                  <div
                    className="menu combo-pop"
                    style={{ position: 'fixed', zIndex: 130, ...pos }}
                  >
                    {search}
                    {list}
                  </div>
                </>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
